/*****************************************************************
 *  CUSTOMER BUTLER - THE TABS UNDER A RECORD                      *
 *  File: apps/web/src/pages/customer/RecordTabs.js                *
 *  What it does: the tab row (Zoho's related lists in Zoho's      *
 *  order, from the layout door), and three tabs:                  *
 *    NotesTab         the office notes board (never deleted)      *
 *    ReportsTab       past reports from the Report Engine         *
 *    NotConnectedTab  the honest "not connected yet" tab          *
 *  The other related lists live in RelatedLists.js.               *
 *  Reads: the office-notes door, the Report Engine lookup door.   *
 *  Writes: office notes (add, take off with a reason).            *
 *****************************************************************/

/*****************************************************************
 *  TABLE OF CONTENTS                                              *
 *  SECTION 1 - THE TAB ROW                                        *
 *  SECTION 2 - NOTES (office notes, taken off but never deleted)  *
 *  SECTION 3 - REPORTS (the Report Engine's lookup door)          *
 *  SECTION 4 - NOT CONNECTED YET                                  *
 *****************************************************************/

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { QuietLine, Notice, StatusChip, Sheet } from './customerParts';
import { formatMoment, formatDay } from './customerDates';
import { fetchOfficeNotes, addOfficeNote, takeOfficeNoteOff, fetchPastReports } from './customerApi';
import { useCustomerAccessView } from './customerAccessView';

/*****************************************************************
 *  SECTION 1 - THE TAB ROW                                        *
 *****************************************************************/

/* ---- TabRow({ tabs, chosenKey, onChoose, countFor }) -----------
   HR's pill tabs. countFor(key) may give a number to show beside
   a tab's name ("Properties 2"); undefined shows none.          */
export function TabRow({ tabs, chosenKey, onChoose, countFor }) {
  return (
    <div className="cb-tabs" role="tablist" aria-label="Record sections">
      {tabs.map((oneTab) => {
        const tabCount = countFor ? countFor(oneTab.key) : undefined;
        const isChosen = oneTab.key === chosenKey;

        return (
          <button
            key={oneTab.key}
            type="button"
            role="tab"
            className="cb-tab"
            aria-selected={isChosen ? 'true' : 'false'}
            onClick={() => onChoose(oneTab.key)}
          >
            {oneTab.label}
            {tabCount !== undefined ? <span className="cb-tab-count">{tabCount}</span> : null}
          </button>
        );
      })}
    </div>
  );
}

/*****************************************************************
 *  SECTION 2 - NOTES                                              *
 *  WHY (2026-10-14, James; readiness defect 364): "Nothing is     *
 *  ever deleted." Taking a note off asks why, keeps the note, and *
 *  the board can still show it, greyed, with who / when / why.    *
 *****************************************************************/

/* ---- TakeOffSheet({ note, onClose, onDone }) -- asks the reason ---- */
function TakeOffSheet({ note, onClose, onDone }) {
  const [reasonText, setReasonText] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [problemWords, setProblemWords] = useState('');

  async function takeItOff(submitEvent) {
    submitEvent.preventDefault();

    if (!reasonText.trim()) {
      setProblemWords('Say why, so the next person knows.');
      return;
    }

    setIsSaving(true);

    const removeAnswer = await onDone(note, reasonText.trim());

    if (!removeAnswer.ok) {
      setIsSaving(false);
      setProblemWords((removeAnswer.error && removeAnswer.error.message) || 'The note was not taken off.');
    }
  }

  return (
    <Sheet title="Take this note off the board" onClose={onClose}>
      <p className="cb-sub">The note is kept, with your name, the time and your reason. It is never deleted.</p>
      <form onSubmit={takeItOff}>
        <label className="cb-label" htmlFor="cb-note-why">Why</label>
        <input id="cb-note-why" className="cb-input" value={reasonText} onChange={(changeEvent) => setReasonText(changeEvent.target.value)} />
        {problemWords ? <p className="cb-edit-error" role="alert">{problemWords}</p> : null}
        <div className="cb-sheet-acts">
          <button type="button" className="bc-btn is-ghost" onClick={onClose}>Cancel</button>
          <button type="submit" className="bc-btn is-primary" disabled={isSaving}>{isSaving ? 'Saving' : 'Take it off'}</button>
        </div>
      </form>
    </Sheet>
  );
}

/* ---- NoteRow({ note, onTakeOff }) -- one note on the board ----
   Exported for the level tests (customerAccessView.test.js).     */
export function NoteRow({ note, onTakeOff }) {
  const wasTakenOff = Boolean(note.removedAt);
  const accessView = useCustomerAccessView(); // only a Manager takes a note off (Honest UI, 2026-10-14)

  return (
    <li className={wasTakenOff ? 'cb-item is-removed' : 'cb-item'}>
      <div className="cb-item-top">
        <span className="cb-item-meta">{note.byName || 'Staff'}, {formatMoment(note.at)}</span>
        {wasTakenOff ? <StatusChip tone="neutral" icon="archive" word="Taken off" /> : null}
        {!wasTakenOff && accessView.mayTakeOff ? (
          <button type="button" className="cb-link-btn" onClick={() => onTakeOff(note)}>Take off</button>
        ) : null}
      </div>
      <div className="cb-item-body">{note.text}</div>
      {wasTakenOff ? (
        <div className="cb-item-meta">
          Taken off by {note.removedByName || 'Staff'}, {formatMoment(note.removedAt)}. Why: {note.removedWhy}
        </div>
      ) : null}
    </li>
  );
}

/* ---- NotesTab({ customerId, onNotesLoaded }) -------------------
   The office-notes board: staff only, never shown to a customer.
   onNotesLoaded (optional): told the whole list every time the
   board loads it - when it opens, after a note is added and after
   one is taken off - so a count elsewhere on the page stays true.
   It is only told; it can never change a note.                  */
export function NotesTab({ customerId, onNotesLoaded }) {
  const [allNotes, setAllNotes] = useState(null);       // null = loading
  const [draftText, setDraftText] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [problemWords, setProblemWords] = useState('');
  const [showRemoved, setShowRemoved] = useState(false);
  const [noteBeingTakenOff, setNoteBeingTakenOff] = useState(null);
  const accessView = useCustomerAccessView(); // Look up reads the board, adds nothing

  // WHY (2026-10-15, Fable/James, round-2 rough edge b): the related-list
  // rail's Notes count was read once when the page opened, so adding or
  // taking off a note left the rail wrong until a reload. The board now
  // tells the page its list each time it loads one. The newest listener is
  // kept in a ref, so a parent that passes a new function on every draw
  // never makes the board load again (no loop).
  const tellThePageRef = useRef(onNotesLoaded); // the page's listener, or undefined
  tellThePageRef.current = onNotesLoaded;

  /* ---- keepTheList(loadedNotes) -- show it, and tell the page ---- */
  const keepTheList = useCallback((loadedNotes) => {
    setAllNotes(loadedNotes);

    const tellThePage = tellThePageRef.current;

    if (typeof tellThePage === 'function') {
      tellThePage(loadedNotes);
    }
  }, []);

  const loadNotes = useCallback(async () => {
    try {
      const loadedNotes = await fetchOfficeNotes(customerId);

      keepTheList(loadedNotes);
    } catch (loadError) {
      setAllNotes([]);
      setProblemWords('The notes could not be loaded.');
    }
  }, [customerId, keepTheList]);

  useEffect(() => {
    loadNotes();
  }, [loadNotes]);

  async function saveNote(submitEvent) {
    submitEvent.preventDefault();

    if (!draftText.trim()) {
      return;
    }

    setIsSaving(true);

    const addAnswer = await addOfficeNote(customerId, draftText.trim());

    setIsSaving(false);

    if (!addAnswer.ok) {
      setProblemWords((addAnswer.error && addAnswer.error.message) || 'The note did not save.');
      return;
    }

    setDraftText('');
    setProblemWords('');
    loadNotes();
  }

  async function finishTakingOff(note, reasonText) {
    const removeAnswer = await takeOfficeNoteOff(customerId, note.id, reasonText);

    if (removeAnswer.ok) {
      setNoteBeingTakenOff(null);
      loadNotes();
    }

    return removeAnswer;
  }

  const liveNotes = (allNotes || []).filter((oneNote) => !oneNote.removedAt);
  const removedNotes = (allNotes || []).filter((oneNote) => oneNote.removedAt);

  return (
    <div className="cb-panel">
      {accessView.mayWrite ? (
        <form className="cb-note-add" onSubmit={saveNote}>
          <label className="cb-label" htmlFor="cb-note-new">Office note (staff only, never on a report)</label>
          <textarea id="cb-note-new" className="cb-textarea" value={draftText} onChange={(changeEvent) => setDraftText(changeEvent.target.value)} />
          <div className="cb-edit-acts">
            <button type="submit" className="bc-btn is-primary" disabled={isSaving || !draftText.trim()}>
              {isSaving ? 'Saving' : 'Add note'}
            </button>
          </div>
        </form>
      ) : null}

      {problemWords ? <Notice tone="bad" icon="warn">{problemWords}</Notice> : null}
      {allNotes === null ? <QuietLine icon="clock">Loading the notes</QuietLine> : null}
      {allNotes !== null && liveNotes.length === 0 ? <QuietLine icon="file">No office notes yet.</QuietLine> : null}

      <ul className="cb-list-rows">
        {liveNotes.map((oneNote) => <NoteRow key={oneNote.id} note={oneNote} onTakeOff={setNoteBeingTakenOff} />)}
      </ul>

      {removedNotes.length > 0 ? (
        <button type="button" className="cb-link-btn" onClick={() => setShowRemoved((showingNow) => !showingNow)}>
          {showRemoved ? 'Hide' : 'Show'} notes taken off ({removedNotes.length})
        </button>
      ) : null}
      {showRemoved ? (
        <ul className="cb-list-rows">
          {removedNotes.map((oneNote) => <NoteRow key={oneNote.id} note={oneNote} onTakeOff={setNoteBeingTakenOff} />)}
        </ul>
      ) : null}

      {noteBeingTakenOff && accessView.mayTakeOff ? (
        <TakeOffSheet note={noteBeingTakenOff} onClose={() => setNoteBeingTakenOff(null)} onDone={finishTakingOff} />
      ) : null}
    </div>
  );
}

/*****************************************************************
 *  SECTION 3 - REPORTS                                            *
 *  WHY (2026-10-14, James): the crew needs a customer's past      *
 *  reports on the customer. They live in the Report Engine; its   *
 *  read-only lookup door is asked by email and address. If the    *
 *  door is missing or says no, the tab says so (honest UI).       *
 *****************************************************************/

// Which chip each report state wears: one chip per row, word + icon.
const REPORT_STATUS_CHIPS = {
  sent: { tone: 'ok', icon: 'check', word: 'Sent' },
  'in review': { tone: 'warn', icon: 'clock', word: 'In review' },
  draft: { tone: 'neutral', icon: 'file', word: 'Draft' },
  cancelled: { tone: 'neutral', icon: 'archive', word: 'Cancelled' },
};

/* ---- ReportRow({ report }) -- one past report ---- */
function ReportRow({ report }) {
  const statusWord = String(report.status || '').toLowerCase();
  const chipLook = REPORT_STATUS_CHIPS[statusWord] || { tone: 'neutral', icon: 'file', word: report.status || 'Unknown' };
  const inspectedWords = report.inspectionDate ? `Inspected ${formatDay(report.inspectionDate)}` : '';
  const sentWords = report.sentAt && statusWord === 'sent' ? `sent ${formatDay(report.sentAt)}` : '';
  const metaWords = [report.type, inspectedWords, sentWords].filter(Boolean).join(', ');

  return (
    <li className="cb-item">
      <div className="cb-item-top">
        {report.url ? (
          <a className="cb-item-name" href={report.url} target="_blank" rel="noopener noreferrer">{report.address || 'Report'}</a>
        ) : (
          <span className="cb-item-name">{report.address || 'Report'}</span>
        )}
        <StatusChip tone={chipLook.tone} icon={chipLook.icon} word={chipLook.word} />
      </div>
      {metaWords ? <div className="cb-item-meta">{metaWords}</div> : null}
    </li>
  );
}

/* ---- ReportsTab({ email, addresses }) -------------------------- */
export function ReportsTab({ email, addresses }) {
  const [reportAnswer, setReportAnswer] = useState(null); // null = loading
  const addressKey = (addresses || []).join('|');           // reload when the addresses change

  useEffect(() => {
    let stillShowing = true;

    fetchPastReports({ email, addresses }).then((answer) => {
      if (stillShowing) {
        setReportAnswer(answer);
      }
    });

    return () => {
      stillShowing = false;
    };
  }, [email, addressKey]); // eslint-disable-line react-hooks/exhaustive-deps

  if (reportAnswer === null) {
    return <div className="cb-panel"><QuietLine icon="clock">Looking for past reports</QuietLine></div>;
  }

  if (reportAnswer.state === 'not-connected') {
    return <div className="cb-panel"><QuietLine icon="link">Past reports not connected yet.</QuietLine></div>;
  }

  if (reportAnswer.state === 'trouble') {
    return <div className="cb-panel"><QuietLine icon="warn">Past reports could not be reached right now. Try again in a minute.</QuietLine></div>;
  }

  if (reportAnswer.state === 'nothing-to-look-for') {
    return <div className="cb-panel"><QuietLine icon="search">Add an email or a property address to find past reports.</QuietLine></div>;
  }

  if (reportAnswer.reports.length === 0) {
    return <div className="cb-panel"><QuietLine icon="file">No past reports found for this email or address.</QuietLine></div>;
  }

  return (
    <div className="cb-panel">
      <ul className="cb-list-rows">
        {reportAnswer.reports.map((oneReport) => <ReportRow key={oneReport.id} report={oneReport} />)}
      </ul>
    </div>
  );
}

/*****************************************************************
 *  SECTION 4 - NOT CONNECTED YET                                  *
 *****************************************************************/

/* ---- NotConnectedTab({ label }) --------------------------------
   Zoho has this related list; Butler has not joined it up yet,
   and says so instead of showing an empty list that pretends.   */
export function NotConnectedTab({ label }) {
  return (
    <div className="cb-panel">
      <QuietLine icon="link">{label} not connected yet. Zoho keeps these for now.</QuietLine>
    </div>
  );
}
